import { IElement } from '@shared/models/project.model';
import { IProposal } from '@shared/models/proposals.model';
import {
  ConversionFactorGroupKey,
  Results,
  ResultsRecord,
} from '@shared/models/unit.model';
import { getConversionFactorValue } from './conversion-factors.helpers';

/**
 * Copies per-element results from source ids to new ids after a duplicate, so CO2/cost UI
 * stays stable until the pipeline refreshes proposal resultsRecord.
 *
 * @param record - Existing proposal results (may be undefined for legacy data)
 * @param elementMap - Old element id → new id from duplicate id map (see getElementIdMap)
 * @returns Updated record when any copy was made; otherwise the original or `{}`
 */
export const mergeDuplicatedResultsIntoRecord = (
  record: ResultsRecord | undefined,
  elementMap: ReadonlyMap<IElement['id'], IElement['id']>,
): ResultsRecord => {
  if (elementMap.size === 0) return record ?? {};

  const base = record ?? {};
  const next: ResultsRecord = { ...base };
  let changed = false;

  for (const [fromId, toId] of elementMap) {
    const src = base[fromId];
    if (!src || Object.keys(src).length === 0) continue;

    const existing = next[toId];
    if (existing && Object.keys(existing).length > 0) continue;

    next[toId] = { ...src };
    changed = true;
  }

  return changed ? next : (record ?? {});
};

/**
 * For one element, pick the {@link Results} from the proposal where
 * {@link getConversionFactorValue} for {@link byProperty} is largest.
 * Tie: first proposal among those with the max value. Empty {@link proposalRecords} yields `{}`.
 */
export const getElementMaxResultsFromProposals = (
  proposalRecords: Record<IProposal['id'], ResultsRecord>,
  elementId: IElement['id'],
  byProperty: keyof Results | ConversionFactorGroupKey,
): Results => {
  const perProposal = Object.values(proposalRecords).map(
    (r) => r[elementId] ?? {},
  );
  if (perProposal.length === 0) return {};

  let best: Results = {};
  let bestValue = -Infinity;
  for (const results of perProposal) {
    const value = getConversionFactorValue(results, byProperty);
    if (value > bestValue) {
      bestValue = value;
      best = results;
    }
  }
  return best;
};

/**
 * One pass over all element ids that appear in any proposal {@link ResultsRecord},
 * mapping each id to {@link getElementMaxResultsFromProposals} for {@link byProperty}.
 */
export const buildElementMaxResultsRecord = (
  proposalRecords: Record<IProposal['id'], ResultsRecord>,
  byProperty: keyof Results | ConversionFactorGroupKey,
): ResultsRecord => {
  const elementIds = new Set<IElement['id']>();
  for (const record of Object.values(proposalRecords)) {
    for (const id of Object.keys(record)) {
      elementIds.add(id);
    }
  }

  const out: ResultsRecord = {};
  for (const elementId of elementIds) {
    out[elementId] = getElementMaxResultsFromProposals(
      proposalRecords,
      elementId,
      byProperty,
    );
  }
  return out;
};
